import { useMemo, useState } from "react";
import { ArrowUpDown, ExternalLink, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useWorkspaceOverview from "../workspace/useWorkspaceOverview";
import StatusBadge from "../components/StatusBadge";

const ISSUE_OPTIONS = [
  "All issues",
  "No owner",
  "No subscription",
  "Missing card limit",
  "Over capacity",
  "Missing owner email",
  "Webhook without owner email",
  "Auth state unresolved",
];

const ACCOUNT_OPTIONS = ["All accounts", "enterprise", "organization", "personal", "unknown"];

function formatAccountType(value) {
  if (!value) return "Unknown";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function sortRows(rows, key, direction) {
  const factor = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = a?.[key];
    const right = b?.[key];

    if (typeof left === "number" || typeof right === "number") {
      return ((Number(left) || 0) - (Number(right) || 0)) * factor;
    }

    return (
      String(left || "").localeCompare(String(right || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * factor
    );
  });
}

export default function IntegrityAuditPage() {
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useWorkspaceOverview();
  const [search, setSearch] = useState("");
  const [issueFilter, setIssueFilter] = useState("All issues");
  const [accountFilter, setAccountFilter] = useState("All accounts");
  const [sortKey, setSortKey] = useState("organizationName");
  const [sortDirection, setSortDirection] = useState("asc");

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = data?.integrity?.rows || [];

    const next = rows.filter((row) => {
      const matchesSearch =
        !query ||
        [row.organizationName, row.ownerName, row.ownerEmail, row.packageName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const matchesIssue =
        issueFilter === "All issues" || (row.issues || []).includes(issueFilter);

      const matchesAccount =
        accountFilter === "All accounts" ||
        String(row.accountType || "").toLowerCase() === accountFilter.toLowerCase();

      return matchesSearch && matchesIssue && matchesAccount;
    });

    return sortRows(next, sortKey, sortDirection);
  }, [data, search, issueFilter, accountFilter, sortKey, sortDirection]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  const openWorkspace = (row) => {
    if (row.accountType === "enterprise") {
      navigate("/admin-v2/enterprises");
      return;
    }

    if (row.accountType === "organization") {
      navigate("/admin-v2/organizations");
      return;
    }

    navigate("/admin-v2/customers");
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-flex tw-flex-col tw-gap-5 xl:tw-flex-row xl:tw-items-end xl:tw-justify-between">
          <div className="tw-max-w-3xl tw-space-y-3">
            <p className="admin-v2-eyebrow">Audit and integrity</p>
            <h1 className="admin-v2-title">Find broken ownership, missing subscriptions, and card limit issues before support feels them.</h1>
            <p className="admin-v2-subtitle">
              This view cross-checks workspace data so Admin 2.0 can show what needs cleanup first.
            </p>
          </div>

          <button type="button" className="admin-v2-action admin-v2-action-secondary" onClick={refresh}>
            <RefreshCw size={15} />
            Refresh audit
          </button>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-2">Flagged workspaces</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{data?.integrity?.totalFindings || 0}</p>
            <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">Rows needing cleanup</p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-2">No owner</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{data?.integrity?.noOwner || 0}</p>
            <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">No valid creator or admin link</p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-2">No subscription</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{data?.integrity?.noSubscription || 0}</p>
            <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">Billing term missing or broken</p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-2">Missing card limit</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{data?.integrity?.missingCardLimit || 0}</p>
            <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">Capacity cannot be trusted</p>
          </div>
        </div>

        <div className="tw-mt-4 tw-grid tw-gap-3 xl:tw-grid-cols-[minmax(0,1.2fr)_220px_220px]">
          <label className="admin-v2-filter-field">
            <span className="admin-v2-eyebrow">Search workspace</span>
            <span className="admin-v2-filter-input-wrap">
              <Search size={16} />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by workspace, owner, email, package..."
              />
            </span>
          </label>

          <label className="admin-v2-filter-field">
            <span className="admin-v2-eyebrow">Issue type</span>
            <select value={issueFilter} onChange={(event) => setIssueFilter(event.target.value)} className="admin-v2-filter-select">
              {ISSUE_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-v2-filter-field">
            <span className="admin-v2-eyebrow">Account type</span>
            <select value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)} className="admin-v2-filter-select">
              {ACCOUNT_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="admin-v2-table-wrap tw-p-3 lg:tw-p-4">
        {loading ? (
          <div className="tw-p-8 admin-v2-text-muted">Loading integrity audit...</div>
        ) : error ? (
          <div className="admin-v2-status-note tw-m-3 tw-p-4 tw-text-sm">{error}</div>
        ) : filteredRows.length === 0 ? (
          <div className="tw-p-8 admin-v2-text-muted">No audit findings matched these filters.</div>
        ) : (
          <>
            <div className="tw-hidden lg:tw-block tw-overflow-x-auto">
              <table className="admin-v2-data-table">
                <thead>
                  <tr>
                    {[
                      ["organizationName", "Workspace"],
                      ["accountType", "Type"],
                      ["ownerName", "Owner"],
                      ["packageName", "Package"],
                      ["usedCards", "Usage"],
                      ["daysLeft", "Renewal"],
                      ["health", "Health"],
                    ].map(([key, label]) => (
                      <th key={key}>
                        <button type="button" className="admin-v2-sort-button" onClick={() => toggleSort(key)}>
                          {label}
                          <ArrowUpDown size={14} />
                        </button>
                      </th>
                    ))}
                    <th>Issues</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="tw-space-y-1">
                          <p className="tw-font-semibold admin-v2-text-strong">{row.organizationName}</p>
                          <p className="tw-text-sm admin-v2-text-muted">{row.ownerEmail || "No owner email"}</p>
                        </div>
                      </td>
                      <td>{formatAccountType(row.accountType)}</td>
                      <td>{row.ownerName || "No owner"}</td>
                      <td>{row.packageName || "-"}</td>
                      <td>
                        <div className="tw-min-w-[180px] tw-space-y-2">
                          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs">
                            <span className="admin-v2-text-muted">{row.usedCards} used</span>
                            <span className="admin-v2-text-strong">{row.remainingCards} left</span>
                          </div>
                          <div className="admin-v2-progress">
                            <span
                              style={{
                                width: `${Number(row.totalCards) > 0 ? Math.min((Number(row.usedCards) / Number(row.totalCards)) * 100, 100) : 0}%`,
                              }}
                            />
                          </div>
                          <p className="tw-text-xs admin-v2-text-muted">{row.totalCards} total cards</p>
                        </div>
                      </td>
                      <td>
                        <div className="tw-space-y-1">
                          <p className="tw-font-semibold admin-v2-text-strong">
                            {typeof row.daysLeft === "number"
                              ? row.daysLeft < 0
                                ? `${Math.abs(row.daysLeft)} overdue`
                                : `${row.daysLeft} days`
                              : "-"}
                          </p>
                          <p className="tw-text-sm admin-v2-text-muted">
                            {row.nextPaymentDate ? new Date(row.nextPaymentDate).toLocaleDateString() : "No renewal"}
                          </p>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={row.health || "Unknown"} />
                      </td>
                      <td>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                          {(row.issues || []).map((issue) => (
                            <span key={`${row.id}-${issue}`} className="admin-v2-inline-meta">
                              {issue}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <button type="button" className="admin-v2-action admin-v2-action-secondary tw-min-h-[38px] tw-px-4" onClick={() => openWorkspace(row)}>
                          Open
                          <ExternalLink size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-v2-data-grid lg:tw-hidden">
              {filteredRows.map((row) => (
                <article key={row.id} className="admin-v2-mobile-card tw-space-y-4">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <div>
                      <p className="tw-text-base tw-font-semibold admin-v2-text-strong">{row.organizationName}</p>
                      <p className="tw-text-sm admin-v2-text-muted">{row.ownerName || "No owner"}</p>
                    </div>
                    <StatusBadge status={row.health || "Unknown"} />
                  </div>

                  <div className="tw-grid tw-gap-3 tw-grid-cols-2">
                    <div>
                      <p className="admin-v2-eyebrow tw-mb-1">Type</p>
                      <p className="admin-v2-text-strong">{formatAccountType(row.accountType)}</p>
                    </div>
                    <div>
                      <p className="admin-v2-eyebrow tw-mb-1">Package</p>
                      <p className="admin-v2-text-strong">{row.packageName || "-"}</p>
                    </div>
                  </div>

                  <div className="admin-v2-progress">
                    <span
                      style={{
                        width: `${Number(row.totalCards) > 0 ? Math.min((Number(row.usedCards) / Number(row.totalCards)) * 100, 100) : 0}%`,
                      }}
                    />
                  </div>

                  <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                    <span className="admin-v2-text-muted">{row.usedCards} used</span>
                    <span className="tw-font-semibold admin-v2-text-strong">{row.remainingCards} left</span>
                  </div>

                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    {(row.issues || []).map((issue) => (
                      <span key={`${row.id}-${issue}-mobile`} className="admin-v2-inline-meta">
                        {issue}
                      </span>
                    ))}
                  </div>

                  <button type="button" className="admin-v2-action admin-v2-action-secondary tw-w-full" onClick={() => openWorkspace(row)}>
                    Open workspace
                    <ExternalLink size={14} />
                  </button>
                </article>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
